import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FEEDBACK_CATEGORIES, FEEDBACK_MAX_LENGTH, FeedbackCategory } from 'lib/feedback';
import { useState } from 'react';
import { submitFeedback } from './feedback';
import { useAuth } from '@/hooks/useAuth';

// 気軽に送ってほしいので、メニューに埋めず常時表示の独立ボタンにする。
export const FeedbackButton = () => {
  const { profile } = useAuth();

  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>('feature');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = content.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= FEEDBACK_MAX_LENGTH && !isSubmitting;

  // 開くたびに空のフォームにする（前回の失敗エラーや入力内容を持ち越さない）。
  const openDialog = () => {
    setError(null);
    setContent('');
    setCategory('feature');
    setOpen(true);
  };

  const closeDialog = () => {
    if (isSubmitting) return;
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await submitFeedback(category, trimmed, profile);
      setContent('');
      setCategory('feature');
      setOpen(false);
      setSent(true);
    } catch (e) {
      console.error(e);
      setError('送信に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button sx={{ textTransform: 'none' }} onClick={openDialog}>
        フィードバック
        <SmsOutlinedIcon fontSize='small' sx={{ ml: 0.5 }} />
      </Button>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth='sm'>
        <DialogTitle sx={{ pr: 6 }}>
          フィードバック
          <IconButton
            onClick={closeDialog}
            disabled={isSubmitting}
            sx={{ position: 'absolute', top: 12, right: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            機能の要望やバグ報告をお送りください。内容を確認してご連絡します。
          </Typography>

          <Stack spacing={2}>
            <FormControl fullWidth size='small'>
              <InputLabel>カテゴリ</InputLabel>
              <Select
                value={category}
                label='カテゴリ'
                onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
                disabled={isSubmitting}
              >
                {FEEDBACK_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={6}
              label='内容'
              placeholder={FEEDBACK_CATEGORIES.find((c) => c.value === category)?.placeholder}
              // ラベルを常に縮小し、フォーカス前から placeholder を表示する
              slotProps={{ inputLabel: { shrink: true } }}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={isSubmitting}
              error={trimmed.length > FEEDBACK_MAX_LENGTH}
              helperText={
                trimmed.length > FEEDBACK_MAX_LENGTH
                  ? `${FEEDBACK_MAX_LENGTH} 文字以内で入力してください (${trimmed.length})`
                  : `${trimmed.length} / ${FEEDBACK_MAX_LENGTH}`
              }
            />

            {error && (
              <Alert severity='error' onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Button
              fullWidth
              variant='contained'
              size='large'
              startIcon={
                isSubmitting ? <CircularProgress size={18} color='inherit' /> : <SendIcon />
              }
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {isSubmitting ? '送信中…' : '送信する'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={sent}
        autoHideDuration={3000}
        onClose={() => setSent(false)}
        message='送信しました。いただいたフィードバックは今後のサービスの改善に役立てさせていただきます。'
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};
